import React, { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import Authorize from '../../components/AuthComponent/Authorize';
import SaveButton from '../../components/Buttons/SaveButton';
import ButtonBar from '../../components/Fields/ButtonBar';
import Form from '../../components/Fields/Form';
import NumberField from '../../components/Fields/NumberField';
import SelectListField from '../../components/Fields/SelectListField';
import TextboxField from '../../components/Fields/TextboxField';
import { FormConfig } from '../../components/Fields/Types';
import useSelectListOptions from '../../components/Hooks/SelectListOptionsHook/useSelectListOptions';
import useValidation from '../../components/Hooks/ValidationHook/useValidation';
import SpinnerOverlay from '../../components/Spinner/SpinnerOverlay';
import { ApplicationFunctions, useAuth } from '../../contexts/authContext';
import { useNotifications } from '../../contexts/notificationContext';
import CodeTaskParameterInfo from '../../models/CodeTaskParameterInfo';
import WorkflowActionCodeTaskModel from '../../models/WorkflowActionCodeTaskModel';
import DataService from '../../services/data.service';
import IdParams from '../../types/IdParams';
import { DataResult } from '../../types/Result';
import Unauthorized from '../Authentication/Unauthorized';
import CodeTaskField from './Controls/CodeTaskField';

const WorkflowActionCodeTaskView: React.FC = () => {
    const codeTaskNames = useSelectListOptions('api/v1/selectlist/CodeTaskNames', '-- Select codetask handler');
    const [serverErrors, setServerErrors] = useState<DataResult<WorkflowActionCodeTaskModel>>({ isSuccesful: true, messages: [], data: undefined });
    const { handleResult, handleException } = useNotifications();
    const [isAuthorized, setIsAuthorized] = useState<boolean>(true);

    const [formData, setFormData] = useState<WorkflowActionCodeTaskModel>();

    let idParams: IdParams = useParams();
    let workflowActionCodeTaskId: number | undefined;
    if (idParams.id && !Number.isNaN(parseInt(idParams.id))) {
        workflowActionCodeTaskId = parseInt(idParams.id);
    }

    useEffect(() => {
        if (workflowActionCodeTaskId !== undefined) {
            DataService.getById<WorkflowActionCodeTaskModel>('api/v1/WorkflowActionCodeTasks/', workflowActionCodeTaskId)
                .then(response => setFormData(response))
                .catch(e => {
                    if (e.response?.data?.status === 401)
                        setIsAuthorized(false);
                    else
                        handleException(e);
                });
        }
    }, [workflowActionCodeTaskId]);

    useEffect(() => {
        getCodeTaskParameterInfo(formData?.codeTaskName)
    }, [formData?.codeTaskName])

    function getCodeTaskParameterInfo(codeTaskName?: string) {
        if (formData && codeTaskName) {
            DataService
                .get<CodeTaskParameterInfo[]>(`api/v1/WorkflowActionCodeTasks/${workflowActionCodeTaskId}/CodeTaskParameterInfos/${codeTaskName}`)
                .then(response => {
                    formData.codeTaskParameterInfos = response
                    setFormData(() => { return { ...formData } })
                })
                .catch(e => handleException(e));
        }
    }

    function submit() {
        if (formData) {
            DataService
                .update<WorkflowActionCodeTaskModel>(`api/v1/WorkflowActionCodeTasks/`, formData)
                .then(response => {
                    handleResult(response);
                    setServerErrors(response);
                    if (response.isSuccesful === true && response.data)
                        setFormData(response.data);
                })
                .catch(e => handleException(e));
        }
    }

    function onFieldChanged(name: string, value: string): void {
        if (formData) {
            name.split('.').reduce((object, propertyName, index, elements) => {
                if (index === elements.length - 1) {
                    object[propertyName] = value;
                    setFormData(() => { return { ...formData } });
                }

                if (object && object[propertyName]) {
                    return object[propertyName];
                }
                return null;
            }, formData);
        }
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'executionOrder', label: 'Execution order' },
            { name: 'codeTaskName', label: 'Name', validationRules: [{ type: 'isRequired' }] },
            { name: 'parameters', label: 'Parameters'},
        ],
        handleSubmit: submit,
        handleFieldChanged: onFieldChanged,
        showErrors: 'always',
    };

    if (formData?.codeTaskParameterInfos) {
        formData.codeTaskParameterInfos.forEach((parInfo, index) => {
            formConfig.fields.push({ name: `codeTaskParameterInfos.${index}.value`, label: parInfo.name })
        })
    }

    const { getLabelProps, getControlProps, getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData, serverErrors);

    if (!workflowActionCodeTaskId || !formData)
        return <SpinnerOverlay />

    const { isAuthorizedForWorkflowDefinition } = useAuth();
    if (!isAuthorizedForWorkflowDefinition(ApplicationFunctions.ViewWorkflowDefinition, formData.workflowAction.workflowStepDefinition.workflowDefinitionId))
        return (<Unauthorized />);

    return (
        <div className='main-content'>
            <Form title='Workflow definition' footer={(
                <Row>
                    <Col md='12'>
                        <ButtonBar offset={2}>
                            <Authorize applicationFunction={ApplicationFunctions.UpdateWorkflowDefinition} /*workflowDefinitionId={formData.workflowDefinitionId}*/>
                                <SaveButton  {...getSubmitButtonProps()} />
                            </Authorize>
                        </ButtonBar>
                    </Col>
                </Row>
            )}>
                <Container fluid>
                    <Row>
                        <Col md='12'>
                            <NumberField {...getFieldProps('executionOrder')} />
                            <SelectListField {...getFieldProps('codeTaskName')} options={codeTaskNames} />
                            {
                                formData.codeTaskParameterInfos.map((parInfo, index) =>
                                    <CodeTaskField {...getFieldProps(`codeTaskParameterInfos.${index}.value`)} codeTaskParamterInfo={parInfo} />
                                )
                            }
                        </Col>
                    </Row>
                </Container>
            </Form>
        </div>
    );
}
export default WorkflowActionCodeTaskView;